import { useRef, useState } from "react";
import { CalendarClock, Trash2, Plus } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { resolveMediaUrl, uploadAsset } from "@/lib/ddfApi";

// Dubai is UTC+4 with no DST.
const dubaiNowLocal = () => new Date(Date.now() + 4 * 3600 * 1000).toISOString().slice(0, 16);
const fmt = (v) => (v ? v.replace("T", " ") : "");
const newId = () => `sb-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`;

function ScheduledRow({ item, onRemove, saving }) {
  const src = resolveMediaUrl(item.url);
  return (
    <div className="flex items-center gap-2 rounded border bg-white p-2" data-testid={`admin-sched-banner-${item.id}`}>
      {item.type === "video" ? (
        <video src={src} className="h-10 w-16 rounded object-cover" muted playsInline />
      ) : (
        <img src={src} alt="" className="h-10 w-16 rounded object-cover" />
      )}
      <div className="flex-1 text-xs leading-tight">
        <div><strong>Goes live:</strong> {fmt(item.goLiveAt)}</div>
        <div className="text-muted-foreground">
          {item.endAt ? `Back to original: ${fmt(item.endAt)}` : "No end date"}
        </div>
      </div>
      <Button size="sm" variant="ghost" onClick={() => onRemove(item.id)} disabled={saving} data-testid={`admin-sched-banner-remove-${item.id}`}>
        <Trash2 size={14} />
      </Button>
    </div>
  );
}

export function ScheduledBanners({ draft, setDraft, persist, saving }) {
  const fileRef = useRef();
  const [goLiveAt, setGoLiveAt] = useState("");
  const [endAt, setEndAt] = useState("");
  const [busy, setBusy] = useState(false);
  const list = [...(draft.scheduledBanners || [])].sort((a, b) => a.goLiveAt.localeCompare(b.goLiveAt));

  const save = (scheduledBanners) => {
    const next = { ...draft, scheduledBanners };
    setDraft(next);
    return persist(next);
  };

  const add = async () => {
    const file = fileRef.current?.files?.[0];
    if (!file) return toast.error("Pick a banner image or video");
    if (!goLiveAt || goLiveAt <= dubaiNowLocal()) return toast.error("Pick a future go-live date & time (Dubai time)");
    if (endAt && endAt <= goLiveAt) return toast.error("End date must be after the go-live date");
    setBusy(true);
    try {
      const asset = await uploadAsset(file);
      await save([...(draft.scheduledBanners || []), { id: newId(), url: asset.url, type: asset.type, goLiveAt, endAt }]);
      fileRef.current.value = "";
      setGoLiveAt("");
      setEndAt("");
    } catch {
      toast.error("Upload failed");
    } finally {
      setBusy(false);
    }
    return undefined;
  };

  const remove = (id) => {
    if (window.confirm("Remove this scheduled banner?")) save((draft.scheduledBanners || []).filter((b) => b.id !== id));
  };

  return (
    <div className="space-y-2 rounded border p-3 bg-slate-50" data-testid="admin-scheduled-banners">
      <Label className="block text-sm font-semibold">
        <CalendarClock size={14} className="inline mr-1" /> Scheduled Banners (Dubai time)
      </Label>
      <Input type="file" accept="image/*,video/*" ref={fileRef} data-testid="admin-sched-banner-file" />
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <div>
          <Label className="block text-xs">Go live on *</Label>
          <Input type="datetime-local" value={goLiveAt} onChange={(e) => setGoLiveAt(e.target.value)} data-testid="admin-sched-banner-golive" />
        </div>
        <div>
          <Label className="block text-xs">Back to original on (optional)</Label>
          <Input type="datetime-local" value={endAt} onChange={(e) => setEndAt(e.target.value)} data-testid="admin-sched-banner-end" />
        </div>
      </div>
      <Button size="sm" onClick={add} disabled={saving || busy} data-testid="admin-sched-banner-add">
        <Plus size={14} /> {busy ? "Uploading…" : "Add to schedule"}
      </Button>
      {list.length === 0 ? (
        <p className="text-[11px] text-muted-foreground" data-testid="admin-sched-banner-empty">No upcoming banners.</p>
      ) : (
        <div className="space-y-2" data-testid="admin-sched-banner-list">
          {list.map((b) => <ScheduledRow key={b.id} item={b} onRemove={remove} saving={saving} />)}
        </div>
      )}
    </div>
  );
}
